/**
 * PredictiveSearch Component
 * @see https://shopify.dev/docs/api/ajax/reference/predictive-search
 *
 * 전체 동작 요약:
 * - 검색 input에 포커스 또는 입력이 발생하면 이벤트를 감지하여 검색어를 처리합니다.
 * - 입력된 검색어로 서버에 AJAX 요청을 보내고, 결과를 받아와서 검색 결과 영역을 렌더링합니다.
 * - 검색 결과는 predictive-search.liquid에서 렌더링된 HTML을 받아와 표시합니다.
 * - 키보드(방향키, 엔터)로 검색 결과를 탐색하거나 선택할 수 있습니다.
 * - 포커스를 잃거나 검색어가 사라지면 검색 결과 영역을 닫습니다.
 *
 * 주요 메서드:
 * - setupEventListeners: 이벤트 리스너 등록
 * - onChange: 검색어 변경 시 동작
 * - getSearchResults: 서버에 검색 요청
 * - renderSearchResults: 결과 표시
 * - open/closeResults: 결과 영역 열기/닫기
 */
class PredictiveSearch extends SearchForm {
  constructor() {
    // 초기 상태 및 참조 변수 설정

    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";

    this.setupEventListeners();
  }

  /**
   * 검색 input 및 컴포넌트에 이벤트 리스너를 등록합니다.
   */
  setupEventListeners() {
    if (this.input.form)
      this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  /**
   * 현재 검색 input의 값을 반환합니다.
   */
  getQuery() {
    return this.input.value.trim();
  }

  /**
   * 검색어가 변경될 때 호출됩니다.
   * - 캐시된 결과를 정리하고, 검색어를 업데이트하며, 검색 요청을 보냅니다.
   */
  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  /**
   * 폼 제출 시(엔터 등) 동작을 제어합니다.
   * - 검색어가 없거나, 결과가 선택된 경우 제출을 막습니다.
   */
  onFormSubmit(event) {
    // if (
    //   !this.getQuery().length ||
    //   this.querySelector('[aria-selected="true"] a')
    // )
    //   event.preventDefault();
  }

  /**
   * 폼 리셋 시(지우기 버튼 등) 검색 상태를 초기화합니다.
   */
  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  /**
   * 검색 input에 포커스될 때 동작합니다.
   * - 검색어가 있으면 검색 결과를 열거나, 새로 요청합니다.
   */
  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  /**
   * 컴포넌트가 포커스를 잃었을 때 검색 결과를 닫습니다.
   */
  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  /**
   * 키보드 입력이 발생할 때 동작합니다.
   * - 방향키, 엔터 등으로 결과를 탐색/선택할 수 있습니다.
   */
  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  /**
   * 방향키 입력 시 input 커서 이동을 막고, 결과 탐색만 가능하게 합니다.
   */
  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  /**
   * 버튼 텍스트 등 UI에 검색어를 반영합니다.
   */
  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]",
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText ?? "a";
    }
  }

  /**
   * 방향키로 검색 결과 항목을 이동합니다.
   */
  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  /**
   * 현재 선택된 검색 결과 항목을 클릭(선택)합니다.
   */
  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]',
    );

    if (selectedOption) selectedOption.click();
  }

  isKor() {
    const userLocale = navigator.language || navigator.userLanguage;
    const countryCode = userLocale.split("-")[1]; // 예: "ko-KR" -> "KR"
    return true;
    // return countryCode === "KR" || countryCode === "KO";
  }

  /**
   * 서버에 AJAX로 검색 요청을 보내고, 결과를 캐시에 저장합니다.
   */
  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const searchFields =
      "author,body,product_type,tag,title,variants.barcode,variants.sku,variants.title,vendor";

    const q = encodeURIComponent(searchTerm);
    const url = this.isKor()
      ? `https://ccsqbxswcknvqxlzecsi.supabase.co/functions/v1/search-proxy?q=${q}`
      : `https://vaxine.kr/search/suggest?q=${q}&locale=en&section_id=predictive-search&resources[options][fields]=${searchFields}`;

    https: fetch(url, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        if (this.isKor()) {
          return response.json().then((data) => {
            try {
              const products = data.resources.results.products;
              const resultsMarkup = products.map(
                (product, index) =>
                  this.createProductListItem(product, index).outerHTML,
              );
              return this.getTemplate(resultsMarkup.join(""));
            } catch (error) {
              console.log("Error parsing JSON response:", error);
              throw new Error("Failed to parse search results");
            }
          });
          // Save bandwidth keeping the cache in all instances synced
        }

        // console.log(response.text());
        return response.text();
      })
      .then((text) => {
        console.log(text);
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          },
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  /**
   * 스크린리더 등 접근성 지원을 위해 로딩 상태를 알립니다.
   */
  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  /**
   * 접근성 지원을 위해 상태 텍스트를 갱신합니다.
   */
  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  /**
   * 받아온 검색 결과 HTML을 DOM에 삽입하고, 결과 영역을 엽니다.
   * 총 검색 결과를 2개로 제한합니다.
   */
  renderSearchResults(resultsMarkup) {
    // 임시 컨테이너에 결과 마크업을 삽입
    const tempContainer = document.createElement("div");
    tempContainer.innerHTML = resultsMarkup;

    // 모든 검색 결과 항목을 찾음
    const allResults = [];

    // 제품 결과 수집
    const productResults = tempContainer.querySelectorAll(
      "#predictive-search-results-products-list > li",
    );
    productResults.forEach((item) => allResults.push(item));

    // 쿼리, 컬렉션, 페이지, 아티클 등 다른 결과 수집
    const otherResults = tempContainer.querySelectorAll('[role="option"]');
    otherResults.forEach((item) => {
      // 제품 결과와 중복되지 않는 항목만 추가
      if (!item.closest("#predictive-search-results-products-list")) {
        allResults.push(item);
      }
    });

    // 결과가 2개 이상이면 2개만 남기고 나머지 제거
    if (allResults.length > 2) {
      for (let i = 2; i < allResults.length; i++) {
        allResults[i].remove();
      }

      // 제품 목록에서 남은 항목 수 확인
      const remainingProducts = tempContainer.querySelectorAll(
        "#predictive-search-results-products-list > li",
      ).length;

      // 제품 목록이 비어있으면 컨테이너 숨김
      if (remainingProducts === 0) {
        const productsList = tempContainer.querySelector(
          "#predictive-search-results-products-list",
        );
        if (productsList && productsList.parentNode) {
          productsList.parentNode.classList.add("tw:hidden");
        }
      }
    }

    // 수정된 마크업을 실제 결과 영역에 삽입
    this.predictiveSearchResults.innerHTML = tempContainer.innerHTML;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  /**
   * 접근성 지원을 위해 검색 결과 수를 알립니다.
   */
  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent,
    );
  }

  /**
   * 검색 결과 영역의 최대 높이를 계산합니다.
   */
  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector(".section-header")?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  // 컴포넌트가 열리는 메소드
  /**
   * 검색 결과 영역을 엽니다.
   * - maxHeight, open 속성, aria-expanded 등을 제어합니다.
   */
  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  /**
   * 검색 결과 영역을 닫고, 필요시 검색어도 초기화합니다.
   */
  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }

  getTemplate(htmlText) {
    return `
<div id="shopify-section-predictive-search" class="shopify-section">

  <div id="predictive-search-results" role="listbox"><div
        id="predictive-search-results-groups-wrapper"
        class="predictive-search__results-groups-wrapper"
      ><div class="predictive-search__result-group"></div><div class="collection page-width"><ul
            id="predictive-search-results-products-list"
            class="grid product-grid grid--2-col tw:pt-[16px] tw:pb-[32px] tw:gap-y-[20px]"
            role="group"
            aria-labelledby="predictive-search-products"
          >${htmlText}</ul>
          <a class="vaxine-button-tertiary" href="/collections/all">VIEW ALL PRODUCTS</a></div></div>

<div class="predictive-search__loading-state">
  <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg>

</div>
</div><span class="hidden" data-predictive-search-live-region-count-value>
    10 results: 3 pages, 4 products
  </span></div>`;
  }

  createProductListItem(product, index) {
    // li 요소 생성
    const li = document.createElement("li");
    li.id = `predictive-search-option-product-${index}`;
    li.className = "grid__item scroll-trigger";
    li.setAttribute("role", "option");
    li.setAttribute("tabindex", "-1");
    li.setAttribute("aria-selected", "false");

    // 이미지 srcset 생성 (다양한 크기)
    const baseSrc = product.featured_image.url;
    const baseUrl = baseSrc.split("?")[0];
    const versionParam = baseSrc.includes("?v=")
      ? `?v=${baseSrc.split("?v=")[1]}`
      : "";

    const srcset = [
      `${baseUrl}${versionParam}&width=165 165w`,
      `${baseUrl}${versionParam}&width=360 360w`,
      `${baseUrl}${versionParam}&width=533 533w`,
      `${baseUrl}${versionParam}&width=720 720w`,
      `${baseUrl}${versionParam}&width=940 940w`,
      `${baseUrl}${versionParam}&width=1066 1066w`,
      `${baseUrl}${versionParam} ${product.featured_image.width}w`,
    ].join(",");

    // HTML 구조 생성
    li.innerHTML = `
        <a href="${product.url}">
            <div class="card-wrapper product-card-wrapper underline-links-hover">
                <div class="card card--standard card--media">
                    <div class="card__inner color-scheme-1 gradient ratio" style="--ratio-percent: 125.0%;">
                        <div class="card__media tw:border tw:rounded-[4px] tw:border-gray-10">
                            <div class="media media--transparent media--hover-effect">
                                <img
                                    srcset="${srcset}"
                                    src="${baseUrl}${versionParam}&width=533"
                                    sizes="(min-width: 1200px) 267px, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 768px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
                                    alt="${product.featured_image.alt}"
                                    class="motion-reduce"
                                    width="${product.featured_image.width}"
                                    height="${product.featured_image.height}"
                                >
                            </div>
                        </div>
                    </div>
                    <div class="card__content">
                        <div class="tw:py-[12px]">
                            <h3 class="tw:font-haffer tw:text-[14px] tw:letter-spacing-[2%] tw:uppercase">
                                ${product.title}
                            </h3>
                            <div class="card-information">
                                <div class="tw:mt-[8px] price">
                                    <div class="price__container">
                                        <div class="price__regular">
                                            <span class="visually-hidden visually-hidden--inline">Regular price</span>
                                            <span class="price-item price-item--regular">
                                                <span class='money'>$${product.price}</span>
                                            </span>
                                        </div>
                                        <div class="price__sale">
                                            <span class="visually-hidden visually-hidden--inline">Regular price</span>
                                            <span>
                                                <s class="price-item price-item--regular">
                                                    <span class='money'>$0.00</span>
                                                </s>
                                            </span>
                                            <span class="visually-hidden visually-hidden--inline">Sale price</span>
                                            <span class="price-item price-item--sale price-item--last">
                                                <span class='money'>$${product.price}</span>
                                            </span>
                                        </div>
                                        <small class="unit-price caption hidden">
                                            <span class="visually-hidden">Unit price</span>
                                            <span class="price-item price-item--last">
                                                <span></span>
                                                <span aria-hidden="true">/</span>
                                                <span class="visually-hidden">&nbsp;per&nbsp;</span>
                                                <span></span>
                                            </span>
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </a>
    `;

    return li;
  }
}

customElements.define("predictive-search", PredictiveSearch);
